/**
 * Phase 130 — compress photos in the browser before they are uploaded or queued offline.
 *
 * Every image (JPEG / PNG / WebP / HEIC where the browser can decode it) becomes a JPEG of at most
 * MAX_IMAGE_BYTES (200 KB): the longest side is capped, then JPEG quality steps down and the picture is
 * downscaled until it fits. Transparency is flattened on white. PDFs and other documents are never touched.
 * The server re-runs the same rule (core/media_compress.py) as the guarantee — this just saves mobile data
 * and keeps the offline photo queue small.
 */

export const MAX_IMAGE_BYTES = 200 * 1024;
const QUALITIES = [0.85, 0.75, 0.65, 0.55, 0.45, 0.35];
const MAX_SIDES = [2000, 1600, 1280, 1024, 800, 640, 480, 360, 240];
const IMAGE_EXT = /\.(jpe?g|png|webp|gif|bmp|tiff?|heic|heif|avif)$/i;

/** True for anything that should go through image compression (by MIME, then by extension). */
export function isImageFile(file) {
  if (!file) return false;
  if (file.type && file.type.startsWith("image/")) return true;
  return !file.type && IMAGE_EXT.test(file.name || "");
}

const jpegName = (name) => `${(name || "photo").replace(/\.[^.]+$/, "") || "photo"}.jpg`;

async function decode(blob) {
  // createImageBitmap honours EXIF orientation in modern browsers; fall back to <img> for older ones
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(blob, { imageOrientation: "from-image" });
    } catch (e) { /* fall through */ }
  }
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Could not read the image")); };
    img.src = url;
  });
}

function draw(source, side) {
  const w = source.width || source.naturalWidth, h = source.height || source.naturalHeight;
  const longest = Math.max(w, h);
  const scale = side < longest ? side / longest : 1;
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(w * scale));
  canvas.height = Math.max(1, Math.round(h * scale));
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff";                 // flatten transparency on white
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
  return canvas;
}

const toJpegBlob = (canvas, q) => new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", q));

/**
 * Compress a File/Blob image to a JPEG Blob <= maxBytes. Returns the original object untouched when it is
 * not an image or cannot be decoded (the server still enforces the cap).
 * Resolves to { blob, file, compressed, originalSize, size, width, height }.
 */
export async function compressImage(input, { maxBytes = MAX_IMAGE_BYTES, filename } = {}) {
  const name = filename || input?.name || "photo.jpg";
  const passthrough = { blob: input, file: input, compressed: false, originalSize: input?.size || 0, size: input?.size || 0 };
  if (!input || !isImageFile(input.name ? input : { type: input.type, name })) return passthrough;
  // already a small JPEG: keep the original bytes (no second lossy generation)
  if (input.type === "image/jpeg" && input.size <= maxBytes) return passthrough;
  let source;
  try {
    source = await decode(input);
  } catch (e) {
    return passthrough;
  }
  const longest = Math.max(source.width || source.naturalWidth, source.height || source.naturalHeight);
  let best = null;
  try {
    for (const side of MAX_SIDES) {
      if (side > longest && best) continue;
      const canvas = draw(source, side);
      for (const q of QUALITIES) {
        const blob = await toJpegBlob(canvas, q);
        if (!blob) continue;
        if (!best || blob.size < best.blob.size) best = { blob, width: canvas.width, height: canvas.height };
        if (blob.size <= maxBytes) {
          return finish(blob, name, input.size, canvas.width, canvas.height);
        }
      }
    }
  } finally {
    if (source && typeof source.close === "function") source.close();
  }
  return best ? finish(best.blob, name, input.size, best.width, best.height) : passthrough;
}

function finish(blob, name, originalSize, width, height) {
  const fname = jpegName(name);
  const file = typeof File === "function" ? new File([blob], fname, { type: "image/jpeg", lastModified: Date.now() }) : blob;
  return { blob, file, compressed: true, originalSize, size: blob.size, width, height, filename: fname };
}

/** Read a Blob as a data URL. */
export const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.onerror = () => reject(new Error("Could not read file"));
  r.readAsDataURL(blob);
});

/** Compress an image File and return it as a JPEG data URL (documents come back unchanged). */
export async function compressToDataUrl(file, opts) {
  const res = await compressImage(file, opts);
  return blobToDataUrl(res.blob);
}

/** Compress an image already held as a data URL (camera captures, cropped avatars, cheque photocopies). */
export async function compressDataUrl(dataUrl, opts = {}) {
  if (!dataUrl || !dataUrl.startsWith("data:image/")) return dataUrl;
  try {
    const blob = await (await fetch(dataUrl)).blob();
    const res = await compressImage(blob, { ...opts, filename: opts.filename || "photo.jpg" });
    return res.compressed ? blobToDataUrl(res.blob) : dataUrl;
  } catch (e) {
    return dataUrl;
  }
}

/** Human-readable size, e.g. "1.8 MB" / "184 KB". */
export const fmtBytes = (n) => (n >= 1024 * 1024 ? `${(n / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
